import React, { useEffect, useState } from 'react';
import { HistoryOutlined } from '@ant-design/icons';
import StatusBadge from './StatusBadge';
import { formatDate, formatEuro, getOrderGroup } from '../../utils/directOrderStatus';
import { API_ORIGIN } from '../../config';

const MIN_CHARS = 3;
const WAIT_MS = 450;

/**
 * Under the supplier field: the direct orders raised with the same supplier in the last 12 months
 * (reference, activity, breakdown, total and contract time frame), from
 * GET /api/direct-orders/supplier-history. Spelling variants such as "Ltd" vs "Limited" match.
 */
const SupplierHistory = ({ supplier }) => {
  const name = (supplier || '').trim();
  const [state, setState] = useState({ status: 'idle', orders: [], for: '' });

  useEffect(() => {
    if (name.length < MIN_CHARS) {
      setState({ status: 'idle', orders: [], for: '' });
      return undefined;
    }
    let live = true;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setState((s) => ({ ...s, status: 'loading' }));
      try {
        const response = await fetch(`${API_ORIGIN}/api/direct-orders/supplier-history?supplier=${encodeURIComponent(name)}`, { signal: controller.signal });
        const data = await response.json();
        if (!live) return;
        if (!response.ok || data.success === false) throw new Error();
        setState({ status: 'done', orders: data.orders || [], for: name });
      } catch (err) {
        if (live && err.name !== 'AbortError') setState({ status: 'error', orders: [], for: name });
      }
    }, WAIT_MS);
    return () => {
      live = false;
      controller.abort();
      clearTimeout(timer);
    };
  }, [name]);

  if (state.status === 'idle') return null;

  if (state.status === 'loading' && state.orders.length === 0) {
    return <p className="do-supplier-history__note" aria-live="polite">Looking for earlier orders with this supplier…</p>;
  }
  if (state.status === 'error') {
    return <p className="do-supplier-history__note" aria-live="polite">Earlier orders with this supplier couldn’t be checked right now.</p>;
  }
  if (state.orders.length === 0) {
    return (
      <p className="do-supplier-history__note" aria-live="polite">
        No direct orders with “{state.for}” in the last 12 months.
      </p>
    );
  }

  const counted = state.orders.filter((o) => getOrderGroup(o) !== 'rejected');
  const total = counted.reduce((sum, o) => sum + (Number(o.total) || 0), 0);
  const rejected = state.orders.length - counted.length;

  return (
    <section className="do-supplier-history" aria-live="polite" aria-labelledby="do-supplier-history-title">
      <header className="do-supplier-history__head">
        <span className="do-supplier-history__icon" aria-hidden="true"><HistoryOutlined /></span>
        <div>
          <h3 className="do-supplier-history__title" id="do-supplier-history-title">
            {state.orders.length} earlier {state.orders.length === 1 ? 'order' : 'orders'} with {state.orders[0].doSupplier || state.for} in the last 12 months
          </h3>
          <p className="do-supplier-history__sum">
            <strong className="do-num">{formatEuro(total)}</strong> in total{rejected > 0 ? `, not counting ${rejected} rejected` : ''}
          </p>
        </div>
      </header>
      <ul className="do-supplier-history__list" aria-label="Earlier orders with this supplier">
        {state.orders.map((o) => (
          <li className={`do-supplier-history__item${getOrderGroup(o) === 'rejected' ? ' is-rejected' : ''}`} key={o.doId}>
            <div className="do-supplier-history__main">
              <div className="do-supplier-history__line">
                <strong className="do-ref">{o.doRef}</strong>
                <span className="do-supplier-history__date">{formatDate(o.doDate)}</span>
                <StatusBadge order={o} />
              </div>
              <div className="do-supplier-history__line">
                <span className="do-supplier-history__activity">{o.activity || 'No activity'}</span>
                <span className="do-supplier-history__until">Contract time frame: {o.doTimeframe || '—'}</span>
              </div>
              {o.doBreakdown && <p className="do-supplier-history__breakdown">{o.doBreakdown}</p>}
            </div>
            <span className="do-supplier-history__total do-num">{o.total == null ? (o.doTotalCost || '—') : formatEuro(o.total)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default SupplierHistory;
